import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '../i18n';
import './LanguageSelector.css';

interface LanguageSelectorProps {
  className?: string;
  showLabel?: boolean;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ 
  className = '', 
  showLabel = true 
}) => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    setIsOpen(false); // Close dropdown after selection
    
    // Store language preference in localStorage
    localStorage.setItem('preferred-language', languageCode);
    
    // Store language preference in user profile if available
    const currentUser = localStorage.getItem('currentUser');
    if (currentUser) {
      try {
        const userData = JSON.parse(currentUser);
        userData.preferences = {
          ...userData.preferences,
          language: languageCode
        };
        localStorage.setItem('currentUser', JSON.stringify(userData));
      } catch (error) {
        console.error('Failed to save language preference:', error);
      }
    }
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const getCountryName = (languageCode: string) => {
    const countryNames: { [key: string]: string } = {
      'pt-BR': 'Brasil',
      'pt-PT': 'Portugal', 
      'en': 'Reino Unido',
      'es': 'Espanha'
    };
    return countryNames[languageCode] || '';
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const currentLanguage = SUPPORTED_LANGUAGES.find(lang => lang.code === i18n.language) 
    || SUPPORTED_LANGUAGES[0];

  return (
    <div className={`language-selector ${className}`} ref={dropdownRef}>
      {showLabel && (
        <label className="language-label">
          {t('common.language')}:
        </label>
      )}
      <div className="language-dropdown">
        <button 
          className="language-current" 
          onClick={toggleDropdown}
          aria-label="Select language"
          aria-expanded={isOpen}
        >
          <span className="language-flag">{currentLanguage.flag}</span>
          {showLabel && <span className="language-name">{currentLanguage.name}</span>}
          <span className={`language-arrow ${isOpen ? 'open' : ''}`}>▼</span>
        </button>
        {isOpen && (
          <div className="language-options">
            {SUPPORTED_LANGUAGES.map((language) => (
              <button
                key={language.code}
                className={`language-option ${i18n.language === language.code ? 'active' : ''}`}
                onClick={() => handleLanguageChange(language.code)}
                aria-label={`Switch to ${language.name}`}
              >
                <span className="language-flag">{language.flag}</span>
                <div className="language-text">
                  <span className="language-name">{language.name}</span>
                  <span className="language-country">{getCountryName(language.code)}</span>
                </div>
                {i18n.language === language.code && (
                  <span className="language-dot"></span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LanguageSelector;
